import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import {MoleculeChemicalFormulaQuery} from 'chemistry/molecules/tabs/__generated/MoleculeChemicalFormulaQuery';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {TEXT_FIELD_BACKGROUND_COLOR, TEXT_FIELD_BORDER} from '../MoleculeConstants';

export const MOLECULE_CHEMICAL_FORMULA_QUERY = gql`
  query MoleculeChemicalFormulaQuery($moleculeId: StrictString!, $moleculeVersionId: StrictString!)
  @skipAudit
  @engComponent(name: CHEMISTRY) {
    molecule(id: $moleculeId) {
      ... on MoleculeNode {
        id
        version(versionId: $moleculeVersionId) {
          id
          chemicalStructure {
            id
            chemicalFormula {
              ... on ChemicalFormulaNode {
                value
              }
              ... on ErrorNode {
                message
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface MoleculeChemicalFormulaProps {
  moleculeId: string;
  moleculeVersionId: string;
}

interface ChemicalFormula {
  __typename: string;
  value: string;
}

interface ChemicalFormulaError {
  __typename: string;
  message: string;
}

function isError(
  chemicalFormula: ChemicalFormula | ChemicalFormulaError
): chemicalFormula is ChemicalFormulaError {
  return chemicalFormula.__typename === 'ErrorNode';
}

export default function MoleculeChemicalFormula(props: MoleculeChemicalFormulaProps): JSX.Element | null {
  return (
    <BenchlingQuery
      query={MOLECULE_CHEMICAL_FORMULA_QUERY}
      variables={{moleculeId: props.moleculeId, moleculeVersionId: props.moleculeVersionId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data: MoleculeChemicalFormulaQuery) => {
        if (isInaccessible(data.molecule)) {
          return (
            <div>You do not have access to view this molecule. Please ask your administrator for access.</div>
          );
        } else if (isError(data.molecule.version.chemicalStructure.chemicalFormula)) {
          return (
            <div>
              <div className={css(styles.fieldHeader)}>Chemical Formula</div>
              <div className={css(styles.fieldValue)}>
                {data.molecule.version.chemicalStructure.chemicalFormula.message}
              </div>
            </div>
          );
        } else {
          return (
            <div>
              <div className={css(styles.fieldHeader)}>Chemical Formula</div>
              <div className={css(styles.fieldValue)}>
                <div>
                  {data.molecule.version.chemicalStructure.chemicalFormula.value
                    .split('')
                    .map((char: string, i: number) =>
                      /^\d+$/.test(char) ? <sub key={`chem-formula-${i}`}>{char}</sub> : char
                    )}
                </div>
              </div>
            </div>
          );
        }
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  fieldHeader: {
    fontWeight: 'bold',
    margin: '4px 0px',
  },
  fieldValue: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '6px 12px',
    background: TEXT_FIELD_BACKGROUND_COLOR,
    border: TEXT_FIELD_BORDER,
    boxSizing: 'border-box',
    borderRadius: '4px',
  },
});
